import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "./api/axios";
import { hasAnyRole, getActiveRole } from "./utils/auth";
import { ThemeProvider as CustomThemeProvider } from "./contexts/ThemeContext";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { lightTheme, darkTheme } from "./theme/muiTheme";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import FullReport from "./pages/FullReport";
import AdminPanel from "./pages/AdminPanel";
import RoleAccessManagement from "./pages/RoleAccessManagement";
import Appraisal from "./pages/Appraisal";
import Logs from "./pages/Logs";
import DatabaseViewer from "./pages/DatabaseViewer";

/* ======================
   DASHBOARD
====================== */

/* ======================================================
   CUSTOM SVG CHARTING COMPONENTS
====================================================== */

function TrendLineChart({ data }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-56 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/10">
        <span className="text-3xl mb-2 font-sans">📊</span>
        <p className="text-sm text-slate-500 dark:text-slate-400 italic font-sans">No YoY trend data recorded yet</p>
      </div>
    );
  }

  const width = 500;
  const height = 240;
  const paddingLeft = 40;
  const paddingRight = 20;
  const paddingTop = 30;
  const paddingBottom = 40;

  const maxVal = Math.max(
    ...data.map((d) => Math.max(d.publications || 0, d.patents || 0, d.copyrights || 0)),
    5
  );

  const getCoordinates = (field) => {
    return data.map((d, index) => {
      const x = paddingLeft + (index * (width - paddingLeft - paddingRight)) / Math.max(data.length - 1, 1);
      const y = height - paddingBottom - ((d[field] || 0) * (height - paddingTop - paddingBottom)) / maxVal;
      return { x, y, val: d[field] || 0, year: d.academic_year };
    });
  };

  const pubPoints = getCoordinates("publications");
  const patPoints = getCoordinates("patents");
  const copyPoints = getCoordinates("copyrights");

  const getPathD = (points) => {
    if (points.length === 0) return "";
    return points.reduce((acc, p, i) => (i === 0 ? `M ${p.x} ${p.y}` : `${acc} L ${p.x} ${p.y}`), "");
  };

  const gridTicks = 5;
  const yTicks = Array.from({ length: gridTicks }, (_, i) => Math.round((maxVal * i) / (gridTicks - 1)));

  return (
    <div className="bg-white dark:bg-slate-800/40 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between">
      <div>
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-1.5 font-sans">
          📈 YoY Progress Trends
        </h3>
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto overflow-visible">
          {/* Grid lines */}
          {yTicks.map((tick, index) => {
            const y = height - paddingBottom - (tick * (height - paddingTop - paddingBottom)) / maxVal;
            return (
              <g key={index} className="opacity-20 dark:opacity-10 text-slate-400">
                <line x1={paddingLeft} y1={y} x2={width - paddingRight} y2={y} stroke="currentColor" strokeWidth={1} strokeDasharray="3,3" />
                <text x={paddingLeft - 8} y={y + 4} textAnchor="end" className="fill-current text-[10px] font-semibold font-sans">{tick}</text>
              </g>
            );
          })}

          {/* X axis labels */}
          {data.map((d, index) => {
            const x = paddingLeft + (index * (width - paddingLeft - paddingRight)) / Math.max(data.length - 1, 1);
            return (
              <text
                key={index}
                x={x}
                y={height - paddingBottom + 18}
                textAnchor="middle"
                className="fill-current text-slate-500 dark:text-slate-400 text-[10px] font-semibold font-sans"
              >
                {d.academic_year}
              </text>
            );
          })}

          {/* Trend Lines */}
          <path d={getPathD(pubPoints)} fill="none" stroke="#6366f1" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
          <path d={getPathD(patPoints)} fill="none" stroke="#10b981" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
          <path d={getPathD(copyPoints)} fill="none" stroke="#a855f7" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

          {/* Marker Dots */}
          {[
            { points: pubPoints, color: "#6366f1" },
            { points: patPoints, color: "#10b981" },
            { points: copyPoints, color: "#a855f7" },
          ].map((line, lineIdx) =>
            line.points.map((p, ptIdx) => (
              <g key={`${lineIdx}-${ptIdx}`} className="group cursor-pointer">
                <circle cx={p.x} cy={p.y} r={4} fill="#fff" stroke={line.color} strokeWidth={2} />
                <circle cx={p.x} cy={p.y} r={10} fill="transparent" />
                <title>{`${p.year}: ${p.val}`}</title>
              </g>
            ))
          )}
        </svg>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-6 mt-5 text-xs font-bold border-t border-slate-100 dark:border-slate-800 pt-3 font-sans">
        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="w-3 h-3 rounded-full bg-indigo-500"></span> Publications
        </span>
        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="w-3 h-3 rounded-full bg-emerald-500"></span> Patents
        </span>
        <span className="flex items-center gap-1.5 text-slate-600 dark:text-slate-300">
          <span className="w-3 h-3 rounded-full bg-purple-500"></span> Copyrights
        </span>
      </div>
    </div>
  );
}

function DepartmentBarChart({ data }) {
  if (!data || data.length === 0) return null;

  const activeDepts = data.filter((d) => (d.publications || 0) + (d.patents || 0) + (d.copyrights || 0) > 0);

  if (activeDepts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-56 border border-dashed border-slate-300 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800/10">
        <span className="text-3xl mb-2 font-sans">🏫</span>
        <p className="text-sm text-slate-500 dark:text-slate-400 italic font-sans">No departmental details found</p>
      </div>
    );
  }

  const maxVal = Math.max(
    ...activeDepts.map((d) => Math.max(d.publications || 0, d.patents || 0, d.copyrights || 0)),
    5
  );

  return (
    <div className="bg-white dark:bg-slate-800/40 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between">
      <div>
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-1.5 font-sans">
          🏫 Comparative Branch Statistics
        </h3>
        <div className="space-y-4 max-h-[190px] overflow-y-auto pr-1">
          {activeDepts.map((dept, index) => {
            const pubPct = Math.max(((dept.publications || 0) / maxVal) * 100, 2);
            const patPct = Math.max(((dept.patents || 0) / maxVal) * 100, 2);
            const copyPct = Math.max(((dept.copyrights || 0) / maxVal) * 100, 2);

            return (
              <div key={index} className="space-y-1 bg-slate-50 dark:bg-slate-800/25 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800">
                <div className="text-[11px] font-bold text-slate-800 dark:text-slate-200 mb-1 flex justify-between font-sans">
                  <span>{dept.department_name}</span>
                  <span className="text-[10px] text-slate-400 font-sans">{dept.department_code}</span>
                </div>
                <div className="space-y-1">
                  {dept.publications > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-8 text-[9px] text-indigo-600 dark:text-indigo-400 font-extrabold text-right font-sans">PUB</span>
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div className="bg-indigo-500 h-full rounded-full transition-all duration-500" style={{ width: `${pubPct}%` }}></div>
                      </div>
                      <span className="w-4 text-[9px] font-bold text-slate-700 dark:text-slate-300 text-right font-sans">{dept.publications}</span>
                    </div>
                  )}
                  {dept.patents > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-8 text-[9px] text-emerald-600 dark:text-emerald-400 font-extrabold text-right font-sans">PAT</span>
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div className="bg-emerald-500 h-full rounded-full transition-all duration-500" style={{ width: `${patPct}%` }}></div>
                      </div>
                      <span className="w-4 text-[9px] font-bold text-slate-700 dark:text-slate-300 text-right font-sans">{dept.patents}</span>
                    </div>
                  )}
                  {dept.copyrights > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="w-8 text-[9px] text-purple-600 dark:text-purple-400 font-extrabold text-right font-sans">CPR</span>
                      <div className="flex-1 bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
                        <div className="bg-purple-500 h-full rounded-full transition-all duration-500" style={{ width: `${copyPct}%` }}></div>
                      </div>
                      <span className="w-4 text-[9px] font-bold text-slate-700 dark:text-slate-300 text-right font-sans">{dept.copyrights}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function DonutChart({ title, data, colors }) {
  const total = data.reduce((acc, d) => acc + d.value, 0);

  if (total === 0) {
    return (
      <div className="bg-white dark:bg-slate-800/40 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-center items-center h-full min-h-[220px]">
        <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2 w-full text-left font-sans">{title}</h3>
        <p className="text-xs text-slate-400 italic my-auto font-sans">No records recorded</p>
      </div>
    );
  }

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  let accumulatedPercent = 0;

  return (
    <div className="bg-white dark:bg-slate-800/40 p-6 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex flex-col justify-between h-full min-h-[220px]">
      <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-3 font-sans">{title}</h3>
      <div className="flex items-center justify-around gap-4 flex-1">
        {/* SVG Circle */}
        <div className="relative w-24 h-24 flex items-center justify-center flex-shrink-0">
          <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
            {/* Background circle */}
            <circle cx="50" cy="50" r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth="10" />
            
            {/* Render segment arcs */}
            {data.map((item, index) => {
              if (item.value === 0) return null;
              const percent = item.value / total;
              const strokeDasharray = `${percent * circumference} ${circumference}`;
              const strokeDashoffset = -accumulatedPercent * circumference;
              accumulatedPercent += percent;

              return (
                <circle
                  key={index}
                  cx="50"
                  cy="50"
                  r={radius}
                  fill="transparent"
                  stroke={colors[index] || "#94a3b8"}
                  strokeWidth="10"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center font-sans">
            <span className="text-lg font-black text-slate-800 dark:text-white leading-none font-sans">{total}</span>
            <span className="text-[8px] font-bold text-slate-400 uppercase tracking-wider mt-0.5 font-sans">Total</span>
          </div>
        </div>

        {/* Legend */}
        <div className="space-y-1.5 text-xs font-semibold flex-1 font-sans">
          {data.map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400 font-sans">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: colors[index] }}></span>
                {item.label}
              </span>
              <span className="text-slate-800 dark:text-slate-200 font-bold font-sans">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const [role, setRole] = useState(getActiveRole());
  const [departmentFaculty, setDepartmentFaculty] = useState([]);
  const [departmentInfo, setDepartmentInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem("token");

  // Stats States
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [deptBreakdown, setDeptBreakdown] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedDeptId, setSelectedDeptId] = useState("");
  const [startYear, setStartYear] = useState("");
  const [endYear, setEndYear] = useState("");
  const [searchName, setSearchName] = useState(""); // debounced value used for queries
  const [searchInput, setSearchInput] = useState(""); // raw text typed by the user
  const [statsLoading, setStatsLoading] = useState(false);

  // Dashboard sub-tab: "research" (stats) or "faculties" (directory)
  const [dashboardTab, setDashboardTab] = useState("research");
  // Whether the current user's role may see the "Faculties" dashboard tab.
  const [canViewFacultyDashboard, setCanViewFacultyDashboard] = useState(true);
  // Toggle between the compact basic faculty list and the full directory.
  const [showFullFaculty, setShowFullFaculty] = useState(false);
  const [facultyList, setFacultyList] = useState([]);
  const [savingWorkStatus, setSavingWorkStatus] = useState(null);
  const [facultyLoading, setFacultyLoading] = useState(false);
  const [facultySort, setFacultySort] = useState({ key: "name", dir: "asc" });
  const [facultyQuery, setFacultyQuery] = useState("");

  // Detail Modal States
  const [activeDetailCategory, setActiveDetailCategory] = useState(null);
  const [detailRecords, setDetailRecords] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  // Drill-down: faculty list for the active category, and the selected faculty.
  const [detailFaculty, setDetailFaculty] = useState([]);
  const [selectedFaculty, setSelectedFaculty] = useState(null); // { user_id, full_name, ... }

  // Step 1: open a category -> load the list of faculty who have records.
  const loadDetailRecords = async (category) => {
    setActiveDetailCategory(category);
    setSelectedFaculty(null);
    setDetailRecords([]);
    setDetailFaculty([]);
    setDetailError(null);
    setDetailLoading(true);
    try {
      let url = `/profile/dashboard-faculty?category=${category}`;
      if (selectedDeptId) url += `&department_id=${selectedDeptId}`;
      if (startYear) url += `&start_year=${startYear}`;
      if (endYear) url += `&end_year=${endYear}`;
      if (searchName) url += `&search=${encodeURIComponent(searchName)}`;

      const res = await axios.get(url, { headers });
      setDetailFaculty(res.data.faculty || []);
    } catch (err) {
      console.error("Error loading faculty list:", err);
      setDetailError("Failed to load faculty list.");
    } finally {
      setDetailLoading(false);
    }
  };

  // Step 2: click a faculty -> load only that faculty's records.
  const loadFacultyRecords = async (faculty) => {
    setSelectedFaculty(faculty);
    setDetailRecords([]);
    setDetailError(null);
    setDetailLoading(true);
    try {
      let url = `/profile/dashboard-records?category=${activeDetailCategory}&faculty_user_id=${faculty.user_id}`;
      if (startYear) url += `&start_year=${startYear}`;
      if (endYear) url += `&end_year=${endYear}`;

      const res = await axios.get(url, { headers });
      setDetailRecords(res.data.records || []);
    } catch (err) {
      console.error("Error loading detail records:", err);
      setDetailError("Failed to load records details.");
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetailModal = () => {
    setActiveDetailCategory(null);
    setSelectedFaculty(null);
    setDetailRecords([]);
    setDetailFaculty([]);
  };

  const headers = {
    Authorization: `Bearer ${token}`
  };

  // Debounce the raw search input -> searchName (used by queries).
  useEffect(() => {
    const timer = setTimeout(() => setSearchName(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (hasAnyRole("hod") && !hasAnyRole("admin", "super_admin", "hr")) {
      loadHODDashboard();
    }
    loadDashboardStats(selectedDeptId, startYear, endYear, searchName);
  }, [role, selectedDeptId, startYear, endYear, searchName]);

  // Load the faculty directory for management/HOD (shown as the list below the
  // dashboard) and whenever the Faculties tab is opened. We always attempt the
  // fetch for these roles so the list is populated regardless of tab state.
  useEffect(() => {
    if (hasAnyRole("admin", "super_admin", "hr", "hod") || dashboardTab === "faculties") {
      loadFacultyDirectory(selectedDeptId, searchName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardTab, selectedDeptId, searchName]);

  // Determine whether this user's role may see the Faculties dashboard tab.
  useEffect(() => {
    let active = true;
    const loadFacultyDashboardAccess = async () => {
      // super_admin always sees it; avoid a request we know the answer to.
      if (hasAnyRole("super_admin")) {
        if (active) setCanViewFacultyDashboard(true);
        return;
      }
      try {
        const res = await axios.get("/profile/my-section-visibility", { headers });
        const allowed = res.data?.section_visibility?.faculty_dashboard;
        // Default to visible if the flag is absent (permissive).
        const visible = allowed === undefined ? true : !!allowed;
        if (active) {
          setCanViewFacultyDashboard(visible);
          // If currently on a now-hidden tab, fall back to research.
          if (!visible) setDashboardTab("research");
        }
      } catch (err) {
        console.error("Error loading faculty dashboard access:", err);
        if (active) setCanViewFacultyDashboard(true);
      }
    };
    loadFacultyDashboardAccess();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const loadHODDashboard = async () => {
    setLoading(true);
    try {
      // Get department info
      const deptRes = await axios.get("/hod/department", { headers });
      setDepartmentInfo(deptRes.data.department);

      // Get department faculty
      const facultyRes = await axios.get("/hod/faculty", { headers });
      setDepartmentFaculty(facultyRes.data.faculty || []);
    } catch (err) {
      console.error("Error loading HOD dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardStats = async (deptId = "", sYear = "", eYear = "", search = "") => {
    setStatsLoading(true);
    try {
      let url = "/profile/dashboard-stats";
      const queryParams = [];
      if (deptId) queryParams.push(`department_id=${deptId}`);
      if (sYear) queryParams.push(`start_year=${sYear}`);
      if (eYear) queryParams.push(`end_year=${eYear}`);
      if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
      
      if (queryParams.length > 0) {
        url += "?" + queryParams.join("&");
      }
      
      const res = await axios.get(url, { headers });
      setStats(res.data.stats);
      setTrends(res.data.trends || []);
      setDeptBreakdown(res.data.deptBreakdown || []);
      if (res.data.departments) {
        setDepartments(res.data.departments);
      }
    } catch (err) {
      console.error("Error loading dashboard stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadFacultyDirectory = async (deptId = "", search = "") => {
    setFacultyLoading(true);
    try {
      let url = "/profile/dashboard-faculty-directory";
      const qp = [];
      if (deptId) qp.push(`department_id=${deptId}`);
      if (search) qp.push(`search=${encodeURIComponent(search)}`);
      if (qp.length) url += "?" + qp.join("&");
      const res = await axios.get(url, { headers });
      setFacultyList(res.data.faculty || []);
    } catch (err) {
      console.error("Error loading faculty directory:", err);
      setFacultyList([]);
    } finally {
      setFacultyLoading(false);
    }
  };

  const isManagement = hasAnyRole("admin", "super_admin", "hr");
  // Acts as a (department-scoped) HOD only when they have hod but no management role.
  const isHodView = hasAnyRole("hod") && !isManagement;

  const renderDetailModal = () => {
    if (!activeDetailCategory) return null;

    const categoryTitles = {
      patents: "Patents & Design Records",
      copyrights: "Copyright Records",
      research_publications: "Research Publications",
      fdp: "FDP & Training Records",
      funding: "External Funding Records",
      consultancy: "Consultancy Revenue Records",
      journal_roles: "Journal Roles",
      external_funding: "External Funding Records",
      consultancy_revenue: "Consultancy Revenue Records",
      phd_scholars: "PhD Scholars",
      mooc_econtent_developed: "MOOC / E-Content Developed",
      swayam_mooc_certifications: "SWAYAM MOOC Certifications",
    };

    // Column definitions for the newer drill-down categories (generic renderer
    // below uses these). Each column: { key, label, type? }.
    const GENERIC_COLUMNS = {
      journal_roles: [
        { key: "role_type", label: "Role" },
        { key: "journal_name", label: "Journal" },
        { key: "level", label: "Level" },
        { key: "academic_year", label: "Academic Year" },
      ],
      external_funding: [
        { key: "agency_name", label: "Agency" },
        { key: "agency_type", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "academic_year", label: "Academic Year" },
      ],
      consultancy_revenue: [
        { key: "title", label: "Industry" },
        { key: "consultancy_type", label: "Type" },
        { key: "amount", label: "Amount" },
        { key: "academic_year", label: "Academic Year" },
      ],
      phd_scholars: [
        { key: "scholar_name", label: "Scholar" },
        { key: "status", label: "Status" },
        { key: "starting_year", label: "Start Year" },
        { key: "ending_year", label: "End Year" },
      ],
      mooc_econtent_developed: [
        { key: "content_type", label: "Type" },
        { key: "content_title", label: "Title" },
        { key: "credits", label: "Credits" },
        { key: "academic_year", label: "Academic Year" },
      ],
      swayam_mooc_certifications: [
        { key: "course_title", label: "Course" },
        { key: "agency_name", label: "Agency" },
        { key: "grade_percentage", label: "Grade/%" },
        { key: "academic_year", label: "Academic Year" },
      ],
    };
    const genericCols = GENERIC_COLUMNS[activeDetailCategory];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4 animate-fade-in">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-3">
              {selectedFaculty && (
                <button
                  onClick={() => { setSelectedFaculty(null); setDetailRecords([]); }}
                  className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-100 px-2 py-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm font-semibold"
                >
                  ← Back
                </button>
              )}
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 font-sans">
                  📂 {categoryTitles[activeDetailCategory] || "Detail Records"}
                  {selectedFaculty && (
                    <span className="text-emerald-600 dark:text-emerald-400"> — {selectedFaculty.full_name || selectedFaculty.email}</span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 font-sans mt-0.5">
                  {selectedFaculty
                    ? "Records for the selected faculty member"
                    : "Select a faculty member to view their records"}
                </p>
              </div>
            </div>
            <button
              onClick={closeDetailModal}
              className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {detailLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                <span className="mt-3 text-slate-500 text-sm font-sans">Loading...</span>
              </div>
            ) : detailError ? (
              <div className="text-center py-16 text-rose-500 font-sans">{detailError}</div>
            ) : !selectedFaculty ? (
              /* ---- Level 1: faculty list ---- */
              detailFaculty.length === 0 ? (
                <div className="text-center py-16 text-slate-500 dark:text-slate-400 font-sans">
                  No faculty found with records for the active filters.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {detailFaculty.map((f) => (
                    <button
                      key={f.user_id}
                      onClick={() => loadFacultyRecords(f)}
                      className="flex items-center justify-between text-left px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 dark:text-white truncate">
                          {f.full_name || f.email || "Unknown"}
                        </p>
                        <p className="text-xs text-slate-400 truncate">
                          {f.department_name || ""}{f.email ? ` • ${f.email}` : ""}
                        </p>
                      </div>
                      <span className="ml-3 shrink-0 inline-flex items-center justify-center min-w-[2rem] h-8 px-2 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200 text-sm font-bold">
                        {f.record_count}
                      </span>
                    </button>
                  ))}
                </div>
              )
            ) : detailRecords.length === 0 ? (
              <div className="text-center py-16 text-slate-500 dark:text-slate-400 font-sans">
                No detailed records found matching active filters.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl">
                <table className="w-full divide-y divide-slate-200 dark:divide-slate-800 text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/40 text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider font-sans">
                    {activeDetailCategory === "patents" && (
                      <tr>
                        <th className="px-5 py-3.5">Patent Name</th>
                        <th className="px-5 py-3.5">Status</th>
                        <th className="px-5 py-3.5">Indexing</th>
                        <th className="px-5 py-3.5">Filing / Application</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Files</th>
                      </tr>
                    )}
                    {activeDetailCategory === "copyrights" && (
                      <tr>
                        <th className="px-5 py-3.5">Copyright Name</th>
                        <th className="px-5 py-3.5">Status</th>
                        <th className="px-5 py-3.5">Registration Date</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                      </tr>
                    )}
                    {activeDetailCategory === "research_publications" && (
                      <tr>
                        <th className="px-5 py-3.5">Publication Title</th>
                        <th className="px-5 py-3.5">Authors & Journal</th>
                        <th className="px-5 py-3.5">Type & Category</th>
                        <th className="px-5 py-3.5">Indexing</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Link</th>
                      </tr>
                    )}
                    {activeDetailCategory === "fdp" && (
                      <tr>
                        <th className="px-5 py-3.5">FDP / Training Title</th>
                        <th className="px-5 py-3.5">FDP Type</th>
                        <th className="px-5 py-3.5">Institution</th>
                        <th className="px-5 py-3.5">Duration & Dates</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Files</th>
                      </tr>
                    )}
                    {activeDetailCategory === "funding" && (
                      <tr>
                        <th className="px-5 py-3.5">Funding Agency</th>
                        <th className="px-5 py-3.5">Agency Type</th>
                        <th className="px-5 py-3.5">Amount Granted</th>
                        <th className="px-5 py-3.5">Year</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Details</th>
                      </tr>
                    )}
                    {activeDetailCategory === "consultancy" && (
                      <tr>
                        <th className="px-5 py-3.5">Industry Name</th>
                        <th className="px-5 py-3.5">Consultancy Type</th>
                        <th className="px-5 py-3.5">Amount Generated</th>
                        <th className="px-5 py-3.5">Year / Date</th>
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Details</th>
                      </tr>
                    )}
                    {genericCols && (
                      <tr>
                        {genericCols.map((c) => (
                          <th key={c.key} className="px-5 py-3.5">{c.label}</th>
                        ))}
                        <th className="px-5 py-3.5">Faculty Member</th>
                        <th className="px-5 py-3.5">Details</th>
                      </tr>
                    )}
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800 text-sm text-slate-700 dark:text-slate-300 font-sans">
                    {detailRecords.map((rec) => {
                      // Resolve reports path
                      const reportLink = isHodView 
                        ? `/report?facultyUserId=${rec.user_id}` 
                        : `/report?profileId=${rec.profile_id}`;

                      return (
                        <tr key={rec.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/35 transition-colors">
                          {activeDetailCategory === "patents" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white max-w-xs truncate" title={rec.title}>
                                {rec.title}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.patent_type} | {rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  rec.status === "Granted" ? "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200" :
                                  rec.status === "Published" ? "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200" :
                                  "bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-200"
                                }`}>
                                  {rec.status}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-xs font-semibold">{rec.patent_indexing}</td>
                              <td className="px-5 py-3.5 text-xs space-y-0.5">
                                {rec.filing_no && <p><span className="text-slate-400 font-medium">File:</span> {rec.filing_no}</p>}
                                {rec.application_no && <p><span className="text-slate-400 font-medium">App:</span> {rec.application_no}</p>}
                                {rec.grant_no && <p><span className="text-slate-400 font-medium">Grant:</span> {rec.grant_no}</p>}
                              </td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                {rec.document_link ? (
                                  <a href={buildProtectedDocumentUrl(rec.document_link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-700 hover:underline dark:text-emerald-400 dark:hover:text-emerald-300 font-semibold gap-1">
                                    📄 View File
                                  </a>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">No File</span>
                                )}
                              </td>
                            </>
                          )}

                          {activeDetailCategory === "copyrights" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white" title={rec.title}>
                                {rec.title}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200">
                                  {rec.status}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-xs text-slate-500 dark:text-slate-400">
                                {rec.date_of_registration ? new Date(rec.date_of_registration).toLocaleDateString() : "N/A"}
                              </td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                            </>
                          )}

                          {activeDetailCategory === "research_publications" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white max-w-xs truncate" title={rec.title}>
                                {rec.title}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5 text-xs">
                                <p className="font-semibold">{rec.journal_name || "N/A"}</p>
                                <p className="text-slate-400 mt-0.5">Authors: {rec.authors || "N/A"}</p>
                              </td>
                              <td className="px-5 py-3.5 text-xs">
                                <p className="font-semibold">{rec.type || "N/A"}</p>
                                <p className="text-slate-400 mt-0.5">Cat: {rec.category || "N/A"}</p>
                              </td>
                              <td className="px-5 py-3.5 text-xs font-semibold">{rec.indexing || "N/A"}</td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                {rec.url ? (
                                  <a href={rec.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-indigo-600 hover:text-indigo-700 hover:underline dark:text-indigo-400 dark:hover:text-indigo-300 font-semibold gap-1">
                                    🔗 Link
                                  </a>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">-</span>
                                )}
                              </td>
                            </>
                          )}

                          {activeDetailCategory === "fdp" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white max-w-xs truncate" title={rec.title}>
                                {rec.title}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                                  rec.fdp_role === "Organized" ? "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200" : "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200"
                                }`}>
                                  {rec.fdp_role}
                                </span>
                              </td>
                              <td className="px-5 py-3.5 text-xs">{rec.institute_name || "N/A"}</td>
                              <td className="px-5 py-3.5 text-xs">
                                <p className="font-semibold">{rec.duration_details || "N/A"}</p>
                                <p className="text-slate-400 mt-0.5">
                                  {rec.start_date ? new Date(rec.start_date).toLocaleDateString() : ""} - {rec.end_date ? new Date(rec.end_date).toLocaleDateString() : ""}
                                </p>
                              </td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                {rec.document_link ? (
                                  <a href={buildProtectedDocumentUrl(rec.document_link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-700 hover:underline dark:text-emerald-400 dark:hover:text-emerald-300 font-semibold gap-1">
                                    📄 View File
                                  </a>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">No File</span>
                                )}
                              </td>
                            </>
                          )}

                          {activeDetailCategory === "funding" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white" title={rec.agency_name}>
                                {rec.agency_name}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5 text-xs">{rec.agency_type}</td>
                              <td className="px-5 py-3.5 font-semibold text-slate-900 dark:text-white">
                                ₹{Number(rec.amount || 0).toLocaleString("en-IN")}
                              </td>
                              <td className="px-5 py-3.5 text-xs">{rec.year}</td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5 text-xs truncate max-w-xs">{rec.letter_details || "N/A"}</td>
                            </>
                          )}

                          {activeDetailCategory === "consultancy" && (
                            <>
                              <td className="px-5 py-3.5 font-medium text-slate-900 dark:text-white" title={rec.title}>
                                {rec.title}
                                <span className="block text-[10px] text-slate-400 mt-0.5">{rec.academic_year}</span>
                              </td>
                              <td className="px-5 py-3.5 text-xs">{rec.consultancy_type}</td>
                              <td className="px-5 py-3.5 font-semibold text-slate-900 dark:text-white">
                                ₹{Number(rec.amount || 0).toLocaleString("en-IN")}
                              </td>
                              <td className="px-5 py-3.5 text-xs">
                                {rec.consultancy_date ? new Date(rec.consultancy_date).toLocaleDateString() : rec.year}
                              </td>
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5 text-xs truncate max-w-xs">{rec.letter_details || "N/A"}</td>
                            </>
                          )}

                          {genericCols && (
                            <>
                              {genericCols.map((c, i) => (
                                <td
                                  key={c.key}
                                  className={`px-5 py-3.5 ${i === 0 ? "font-medium text-slate-900 dark:text-white" : "text-xs"}`}
                                  title={rec[c.key]}
                                >
                                  {rec[c.key] !== null && rec[c.key] !== undefined && rec[c.key] !== "" ? String(rec[c.key]) : "N/A"}
                                </td>
                              ))}
                              <td className="px-5 py-3.5">
                                <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400 dark:hover:text-blue-300">
                                  {rec.full_name || "N/A"}
                                </Link>
                                <span className="block text-xs text-slate-400">{rec.email}</span>
                              </td>
                              <td className="px-5 py-3.5">
                                {rec.document_link ? (
                                  <a href={buildProtectedDocumentUrl(rec.document_link)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-xs text-emerald-600 hover:text-emerald-700 hover:underline dark:text-emerald-400 dark:hover:text-emerald-300 font-semibold gap-1">
                                    📄 View File
                                  </a>
                                ) : (
                                  <span className="text-xs text-slate-400 italic">—</span>
                                )}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-800 flex justify-end">
            <button
              onClick={() => setActiveDetailCategory(null)}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold rounded-xl text-sm transition-colors font-sans"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const buildProtectedDocumentUrl = (documentPath) => {
    if (!documentPath) return null;
    if (/^https?:\/\//i.test(documentPath)) return documentPath;
    return `${documentPath}?token=${encodeURIComponent(token || "")}`;
  };

  const renderFacultyDirectory = () => {
    const fmtDate = (d) => {
      if (!d) return "—";
      const dt = new Date(d);
      return isNaN(dt.getTime())
        ? "—"
        : dt.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
    };
    const ym = (obj) => (obj && obj.label ? obj.label : "—");
    const ymValue = (obj) => (obj ? (Number(obj.years) || 0) * 12 + (Number(obj.months) || 0) : 0);

    const canEditWorkStatus = hasAnyRole("super_admin");
    const saveWorkStatus = async (userId, value) => {
      setSavingWorkStatus(userId);
      // optimistic update
      setFacultyList((prev) =>
        prev.map((row) => (row.user_id === userId ? { ...row, work_status: value } : row))
      );
      try {
        await axios.put(
          `/profile/faculty/${userId}/work-status`,
          { work_status: value || null },
          { headers }
        );
      } catch (err) {
        console.error("Error updating work status:", err);
        alert("Failed to update work status. Reloading list.");
        loadFacultyDirectory(selectedDeptId || "", facultyQuery || "");
      } finally {
        setSavingWorkStatus(null);
      }
    };

    if (facultyLoading) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
          <span className="ml-3 text-slate-500 dark:text-slate-400 text-sm font-sans">Loading faculty...</span>
        </div>
      );
    }

    if (!facultyList.length) {
      return (
        <div className="text-center py-16 text-slate-500 dark:text-slate-400 font-sans">
          No faculty found{selectedDeptId ? " for this branch" : ""}.
        </div>
      );
    }

    // ---- summary metrics ----
    const totalFaculty = facultyList.length;
    const phdCount = facultyList.filter((f) => f.phd).length;
    const workingCount = facultyList.filter((f) => f.work_status === "Working").length;

    // ---- client-side quick filter ----
    const q = facultyQuery.trim().toLowerCase();
    const visible = q
      ? facultyList.filter((f) =>
          [f.full_name, f.email, f.department_code, f.department_name, f.current_designation, f.faculty_type]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : facultyList.slice();

    // ---- client-side sort ----
    const sortAccessors = {
      name: (f) => (f.full_name || f.email || "").toLowerCase(),
      department: (f) => (f.department_code || "").toLowerCase(),
      age: (f) => ymValue(f.age_ym),
      institute: (f) => ymValue(f.institute_exp_ym),
      total: (f) => ymValue(f.total_exp_ym),
      joining: (f) => (f.date_of_joining ? new Date(f.date_of_joining).getTime() : 0),
    };
    const acc = sortAccessors[facultySort.key] || sortAccessors.name;
    visible.sort((a, b) => {
      const av = acc(a);
      const bv = acc(b);
      let cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return facultySort.dir === "asc" ? cmp : -cmp;
    });

    const toggleSort = (key) =>
      setFacultySort((prev) =>
        prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }
      );
    const sortArrow = (key) => (facultySort.key === key ? (facultySort.dir === "asc" ? " ▲" : " ▼") : "");

    // ---- chip color helpers ----
    const chip = (text, classes) => (
      <span className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${classes}`}>
        {text}
      </span>
    );
    const deptChip = (code) =>
      code ? chip(code, "bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200") : "—";
    const phdChip = (has) =>
      has
        ? chip("Yes", "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200")
        : chip("No", "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400");
    const workChip = (status) => {
      if (!status) return "—";
      const cls = status === "Working"
        ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
        : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300";
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold whitespace-nowrap ${cls}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${status === "Working" ? "bg-emerald-500" : "bg-slate-400"}`} />
          {status}
        </span>
      );
    };
    const typeChip = (t) =>
      t ? chip(t, "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-200") : "—";

    const th = "px-3 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide whitespace-nowrap";
    const thBtn = "cursor-pointer select-none hover:text-blue-600 dark:hover:text-blue-400 transition-colors";
    const td = "px-3 py-2.5 text-sm text-slate-800 dark:text-slate-200 align-middle whitespace-nowrap";
    // Sticky Name column so identity stays visible while scrolling horizontally.
    const stickyName = "sticky left-0 z-10 bg-inherit";

    const SummaryCard = ({ label, value, accent }) => (
      <div className="flex-1 min-w-[130px] rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/50 px-4 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">{label}</p>
        <p className={`text-2xl font-extrabold mt-0.5 ${accent}`}>{value}</p>
      </div>
    );

    return (
      <div className="space-y-4">
        {/* Summary bar */}
        <div className="flex flex-wrap gap-3">
          <SummaryCard label="Total Faculty" value={totalFaculty} accent="text-slate-900 dark:text-white" />
          <SummaryCard label="With PhD" value={phdCount} accent="text-emerald-600 dark:text-emerald-400" />
          <SummaryCard label="Currently Working" value={workingCount} accent="text-amber-600 dark:text-amber-400" />
        </div>

        {/* Toolbar: quick filter + sort */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-sm">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">🔍</span>
            <input
              type="text"
              value={facultyQuery}
              onChange={(e) => setFacultyQuery(e.target.value)}
              placeholder="Filter by name, dept, designation..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 font-sans"
            />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500 dark:text-slate-400 font-medium">Sort:</span>
            <select
              value={`${facultySort.key}:${facultySort.dir}`}
              onChange={(e) => {
                const [key, dir] = e.target.value.split(":");
                setFacultySort({ key, dir });
              }}
              className="px-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 font-sans"
            >
              <option value="name:asc">Name (A→Z)</option>
              <option value="name:desc">Name (Z→A)</option>
              <option value="department:asc">Department</option>
              <option value="age:desc">Age (high→low)</option>
              <option value="age:asc">Age (low→high)</option>
              <option value="institute:desc">Institute Exp (high→low)</option>
              <option value="total:desc">Total Exp (high→low)</option>
              <option value="joining:asc">Joining Date (oldest)</option>
              <option value="joining:desc">Joining Date (newest)</option>
            </select>
          </div>
          <span className="ml-auto text-xs text-slate-400 font-sans">
            Showing {visible.length} of {totalFaculty}
          </span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
          <table className="min-w-full border-collapse">
            <thead className="bg-slate-100 dark:bg-slate-800 sticky top-0 z-20">
              <tr>
                <th className={`${th} ${stickyName} bg-slate-100 dark:bg-slate-800`} style={{ minWidth: 60 }}>#</th>
                <th className={`${th} ${thBtn} sticky left-[52px] z-10 bg-slate-100 dark:bg-slate-800`} onClick={() => toggleSort("name")}>Name{sortArrow("name")}</th>
                <th className={`${th} ${thBtn}`} onClick={() => toggleSort("department")}>Department{sortArrow("department")}</th>
                <th className={th}>PhD</th>
                <th className={th}>PhD Completed</th>
                <th className={th}>Work Status</th>
                <th className={th}>Designation</th>
                <th className={`${th} ${thBtn}`} onClick={() => toggleSort("age")}>Age{sortArrow("age")}</th>
                <th className={th}>Type</th>
                <th className={`${th} ${thBtn}`} onClick={() => toggleSort("joining")}>Joining Date{sortArrow("joining")}</th>
                <th className={`${th} ${thBtn}`} onClick={() => toggleSort("institute")}>Institute Exp{sortArrow("institute")}</th>
                <th className={th}>Started Teaching</th>
                <th className={`${th} ${thBtn}`} onClick={() => toggleSort("total")}>Total Exp{sortArrow("total")}</th>
                <th className={th}>Other Degree</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((f, idx) => {
                const reportLink =
                  isHodView
                    ? `/report?facultyUserId=${f.user_id}`
                    : `/report?profileId=${f.profile_id}`;
                const rowBg = idx % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/70 dark:bg-slate-900/40";
                return (
                  <tr key={f.user_id} className={`${rowBg} hover:bg-blue-50 dark:hover:bg-slate-800/60 transition-colors border-t border-slate-100 dark:border-slate-800`}>
                    <td className={`${td} ${stickyName} text-slate-400 font-medium`}>{idx + 1}</td>
                    <td className={`${td} sticky left-[52px] z-10 ${rowBg}`}>
                      <Link to={reportLink} className="font-semibold text-blue-600 hover:text-blue-700 hover:underline dark:text-blue-400">
                        {f.full_name || f.email || "Unnamed"}
                      </Link>
                    </td>
                    <td className={td}>{deptChip(f.department_code)}</td>
                    <td className={td}>{phdChip(f.phd)}</td>
                    <td className={`${td} text-slate-600 dark:text-slate-400`}>{f.phd_year || "—"}</td>
                    <td className={td}>
                      {canEditWorkStatus ? (
                        <select
                          value={f.work_status || ""}
                          disabled={savingWorkStatus === f.user_id}
                          onChange={(e) => saveWorkStatus(f.user_id, e.target.value)}
                          className="px-2 py-1 text-[12px] rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 font-sans disabled:opacity-50"
                        >
                          <option value="">— Set —</option>
                          <option value="Working">Working</option>
                          <option value="Retired">Retired</option>
                          <option value="Resigned">Resigned</option>
                        </select>
                      ) : (
                        workChip(f.work_status)
                      )}
                    </td>
                    <td className={td}>
                      {f.current_designation
                        ? chip(f.current_designation, "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200")
                        : "—"}
                    </td>
                    <td className={`${td} font-semibold text-right tabular-nums`}>{ym(f.age_ym)}</td>
                    <td className={td}>{typeChip(f.faculty_type)}</td>
                    <td className={`${td} text-slate-600 dark:text-slate-400`}>{fmtDate(f.date_of_joining)}</td>
                    <td className={`${td} font-semibold text-emerald-700 dark:text-emerald-300 text-right tabular-nums`}>
                      {ym(f.institute_exp_ym)}
                      {f.institute_stints > 1 && (
                        <span className="ml-1 text-[10px] font-normal text-emerald-500">({f.institute_stints})</span>
                      )}
                    </td>
                    <td className={`${td} text-slate-600 dark:text-slate-400`}>{fmtDate(f.started_teaching)}</td>
                    <td className={`${td} font-semibold text-right tabular-nums`}>{ym(f.total_exp_ym)}</td>
                    <td className={`${td} max-w-[220px] whitespace-normal text-xs text-slate-600 dark:text-slate-400`}>{f.other_degree || "—"}</td>
                  </tr>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={14} className="px-4 py-10 text-center text-slate-400 text-sm">
                    No faculty match "{facultyQuery}".
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Compact faculty list — only BASIC details (name, department, designation).
  // Shown below the research dashboard. Full details live in the Faculties tab.
  const renderFacultyBasicList = () => {
    if (facultyLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
          <span className="ml-3 text-slate-500 dark:text-slate-400 text-sm font-sans">Loading faculty...</span>
        </div>
      );
    }
    if (!facultyList.length) {
      return (
        <div className="text-center py-8 text-slate-500 dark:text-slate-400 text-sm font-sans">
          No faculty found{selectedDeptId ? " for this branch" : ""}.
        </div>
      );
    }

    const q = facultyQuery.trim().toLowerCase();
    const rows = (q
      ? facultyList.filter((f) =>
          [f.full_name, f.email, f.department_code, f.department_name, f.current_designation]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q))
        )
      : facultyList
    ).slice().sort((a, b) =>
      (a.full_name || a.email || "").localeCompare(b.full_name || b.email || "")
    );

    const thC = "px-4 py-2.5 text-left text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wide whitespace-nowrap";
    const tdC = "px-4 py-2.5 text-sm text-slate-800 dark:text-slate-200 align-middle";

    return (
      <div className="space-y-3">
        <div className="relative max-w-sm">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">🔍</span>
          <input
            type="text"
            value={facultyQuery}
            onChange={(e) => setFacultyQuery(e.target.value)}
            placeholder="Filter by name, department, designation..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 font-sans"
          />
        </div>
        <div className="overflow-x-auto border border-slate-200 dark:border-slate-800 rounded-xl shadow-sm">
          <table className="min-w-full border-collapse">
            <thead className="bg-slate-100 dark:bg-slate-800">
              <tr>
                <th className={thC} style={{ width: 50 }}>#</th>
                <th className={thC}>Name</th>
                <th className={thC}>Department</th>
                <th className={thC}>Designation</th>
                <th className={thC}>Profile</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((f, idx) => {
                const reportLink = isHodView
                  ? `/report?facultyUserId=${f.user_id}`
                  : `/report?profileId=${f.profile_id}`;
                const rowBg = idx % 2 === 0 ? "bg-white dark:bg-slate-900" : "bg-slate-50/70 dark:bg-slate-900/40";
                return (
                  <tr key={f.user_id} className={`${rowBg} hover:bg-blue-50 dark:hover:bg-slate-800/60 transition-colors border-t border-slate-100 dark:border-slate-800`}>
                    <td className={`${tdC} text-slate-400`}>{idx + 1}</td>
                    <td className={`${tdC} font-semibold text-slate-900 dark:text-white`}>{f.full_name || f.email || "Unnamed"}</td>
                    <td className={tdC}>{f.department_name || f.department_code || "—"}</td>
                    <td className={tdC}>{f.current_designation || "—"}</td>
                    <td className={tdC}>
                      <Link to={reportLink} className="text-blue-600 hover:text-blue-700 hover:underline font-medium dark:text-blue-400">
                        View Profile
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No faculty match "{facultyQuery}".</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderDashboardTabs = () => (
    <div className="flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 mb-5">
      {[
        { key: "research", label: "📊 Research" },
        ...(canViewFacultyDashboard ? [{ key: "faculties", label: "👥 Faculties" }] : []),
      ].map((t) => (
        <button
          key={t.key}
          onClick={() => setDashboardTab(t.key)}
          className={`px-4 py-2 text-sm font-semibold font-sans -mb-px border-b-2 transition-colors ${
            dashboardTab === t.key
              ? "border-emerald-600 text-emerald-700 dark:text-emerald-400"
              : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
          }`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  const renderStatsSection = () => {
    if (statsLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-600 dark:border-slate-400"></div>
          <span className="ml-3 text-slate-500 dark:text-slate-400 text-sm font-sans">Loading statistics...</span>
        </div>
      );
    }
    if (!stats) return null;

    const cardClass = "bg-white dark:bg-slate-900/45 border border-slate-200/80 dark:border-slate-800/80 rounded-2xl p-6 hover:border-slate-400 dark:hover:border-slate-600 hover:shadow-md transition-all cursor-pointer flex flex-col justify-between min-h-[220px]";

    return (
      <div className="space-y-6 mb-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-md font-bold text-slate-800 dark:text-slate-200 uppercase tracking-wider font-sans">📈 Key Indicators Overview</h2>
            <span className="text-xs text-slate-400 font-sans italic">Click cards to drill down into records</span>
          </div>
          
          {/* Search box visible for HOD/Management */}
          {(isManagement || isHodView) && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <span className="text-slate-400 font-sans">🔍</span>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search faculty name..."
                className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-shadow font-sans w-full sm:w-60 shadow-sm"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput("")}
                  className="px-2 py-1 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-xs font-bold text-slate-600 dark:text-slate-300 rounded-lg transition-colors font-sans"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>
        
        {/* Core Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Research Publications Card */}
          <div onClick={() => loadDetailRecords("research_publications")} className={cardClass}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider font-sans">Research Publications</p>
                <p className="text-4xl font-extrabold text-slate-800 dark:text-white mt-2 font-sans leading-none">
                  {stats.research_publications.total}
                </p>
              </div>
              <span className="text-2xl p-2 bg-indigo-50 dark:bg-indigo-950/30 rounded-xl">📚</span>
            </div>
            
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800/60 pt-3 space-y-1.5 font-sans">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Publication Indexing Breakdowns:</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
                <div className="flex justify-between">
                  <span>WOS:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.research_publications.wos || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Scopus:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.research_publications.scopus || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Scopus + WOS:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.research_publications.scopus_wos || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Other:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.research_publications.other || 0}</span>
                </div>
              </div>
              <div className="flex gap-4 text-[10px] text-slate-400 italic font-semibold mt-1">
                <span>Journals: {stats.research_publications.journals || 0}</span>
                <span>Conferences: {stats.research_publications.conferences || 0}</span>
              </div>
            </div>
          </div>

          {/* Patents Stat Card */}
          <div onClick={() => loadDetailRecords("patents")} className={cardClass}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider font-sans">Patents & Design</p>
                <p className="text-4xl font-extrabold text-slate-800 dark:text-white mt-2 font-sans leading-none">
                  {stats.patents.total}
                </p>
              </div>
              <span className="text-2xl p-2 bg-emerald-50 dark:bg-emerald-950/30 rounded-xl">💡</span>
            </div>
            
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800/60 pt-3 space-y-1.5 font-sans">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Status vs Type Breakdown:</p>
              <div className="space-y-1 text-xs text-slate-500 dark:text-slate-400 font-medium">
                <div className="flex justify-between border-b border-slate-50 dark:border-slate-800/30 pb-0.5">
                  <span className="font-semibold text-slate-600 dark:text-slate-400">Filed:</span>
                  <strong className="text-slate-700 dark:text-slate-300">{stats.patents.filed || 0}</strong>
                </div>
                <div className="flex justify-between border-b border-slate-50 dark:border-slate-800/30 pb-0.5">
                  <span className="font-semibold text-slate-600 dark:text-slate-400">Published:</span>
                  <strong className="text-slate-700 dark:text-slate-300">{stats.patents.published || 0}</strong>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-slate-600 dark:text-slate-400">Granted:</span>
                  <strong className="text-slate-700 dark:text-slate-300">{stats.patents.granted || 0}</strong>
                </div>
              </div>
            </div>
          </div>

          {/* Copyrights Stat Card */}
          <div onClick={() => loadDetailRecords("copyrights")} className={cardClass}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider font-sans">Copyrights</p>
                <p className="text-4xl font-extrabold text-slate-800 dark:text-white mt-2 font-sans leading-none">
                  {stats.copyrights.total}
                </p>
              </div>
              <span className="text-2xl p-2 bg-purple-50 dark:bg-purple-950/30 rounded-xl">📝</span>
            </div>
            
            <div className="mt-4 border-t border-slate-100 dark:border-slate-800/60 pt-3 space-y-1.5 font-sans">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">Status Breakdowns:</p>
              <div className="grid grid-cols-1 gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
                <div className="flex justify-between">
                  <span>Filed:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.copyrights.filed || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Registered:</span>
                  <span className="font-bold text-slate-700 dark:text-slate-300">{stats.copyrights.registered || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Compact secondary indicators — click to drill down. Dense grid so
            everything stays visible without extra scrolling. */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { key: "journal_roles", label: "Journal Roles", icon: "📰", color: "bg-sky-50 dark:bg-sky-950/30" },
            { key: "external_funding", label: "External Funding", icon: "💰", color: "bg-amber-50 dark:bg-amber-950/30" },
            { key: "consultancy_revenue", label: "Consultancy Revenue", icon: "🤝", color: "bg-teal-50 dark:bg-teal-950/30" },
            { key: "phd_scholars", label: "PhD Scholars", icon: "🎓", color: "bg-violet-50 dark:bg-violet-950/30" },
            { key: "mooc_econtent_developed", label: "MOOC / E-Content", icon: "💻", color: "bg-rose-50 dark:bg-rose-950/30" },
            { key: "swayam_mooc_certifications", label: "SWAYAM MOOC", icon: "📜", color: "bg-cyan-50 dark:bg-cyan-950/30" },
          ].map((c) => (
            <div
              key={c.key}
              onClick={() => loadDetailRecords(c.key)}
              className="bg-white dark:bg-slate-900/45 border border-slate-200/80 dark:border-slate-800/80 rounded-xl p-4 hover:border-slate-400 dark:hover:border-slate-600 hover:shadow-md transition-all cursor-pointer flex flex-col gap-2"
            >
              <div className="flex items-center justify-between">
                <span className={`text-lg p-1.5 rounded-lg ${c.color}`}>{c.icon}</span>
                <span className="text-2xl font-extrabold text-slate-800 dark:text-white font-sans leading-none">
                  {stats[c.key]?.total ?? 0}
                </span>
              </div>
              <p className="text-[11px] font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wide font-sans leading-tight">
                {c.label}
              </p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // 1. Management Dashboard (Super Admin, Admin, HR)
  if (isManagement) {
    return (
      <>
        <div className="card p-8 animate-fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-200 dark:border-slate-800 pb-5">
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight font-sans">
              {role.replace("_", " ")} Dashboard
            </h1>
            <p className="text-sm text-slate-500 mt-0.5 font-sans">Aggregate research and statistics report</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-sans">Filter Branch:</label>
              <select
                value={selectedDeptId}
                onChange={(e) => {
                  setSelectedDeptId(e.target.value);
                }}
                className="px-3 py-1.5 border border-slate-300 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-shadow font-sans"
              >
                <option value="">All Branches</option>
                {departments.map((dept) => (
                  <option key={dept.id} value={dept.id}>
                    {dept.name} ({dept.code})
                  </option>
                ))}
              </select>
            </div>

            {/* Academic Year range — start and end grouped together */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 font-sans">Academic Year:</label>
              <div className="flex items-center rounded-lg border border-slate-300 dark:border-slate-700 overflow-hidden bg-white dark:bg-slate-800 shadow-sm">
                <select
                  value={startYear}
                  onChange={(e) => setStartYear(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-transparent text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-shadow font-sans"
                >
                  <option value="">From</option>
                  {Array.from({ length: new Date().getFullYear() - 1999 }, (_, i) => String(2000 + i)).map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ))}
                </select>
                <span className="px-1 text-slate-400 select-none">–</span>
                <select
                  value={endYear}
                  onChange={(e) => setEndYear(e.target.value)}
                  className="px-3 py-1.5 text-sm bg-transparent text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500 transition-shadow font-sans"
                >
                  <option value="">To</option>
                  {Array.from({ length: new Date().getFullYear() - 1999 }, (_, i) => String(2000 + i)).map((yr) => (
                    <option key={yr} value={yr}>{yr}</option>
                  ))}
                </select>
              </div>
              {(startYear || endYear) && (
                <button
                  onClick={() => { setStartYear(""); setEndYear(""); }}
                  className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-xs font-semibold text-slate-600 dark:text-slate-300 rounded-lg transition-colors font-sans"
                >
                  Reset
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Research / Faculties tabs (as in the working dashboard). */}
        {renderDashboardTabs()}

        {dashboardTab === "faculties" && canViewFacultyDashboard ? (
          /* Faculties tab → full faculty directory. */
          renderFacultyDirectory()
        ) : (
          <>
            {/* Research dashboard (Key Indicators Overview). */}
            {renderStatsSection()}

            {/* Below the research dashboard: the faculties list (basic details). */}
            <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-slate-800 dark:text-slate-200 font-sans">👥 Faculties</h2>
                <button
                  onClick={() => setShowFullFaculty((v) => !v)}
                  className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 hover:underline"
                >
                  {showFullFaculty ? "← Show basic list" : "View full details →"}
                </button>
              </div>
              {showFullFaculty ? renderFacultyDirectory() : renderFacultyBasicList()}
            </div>
          </>
        )}
        </div>
        {renderDetailModal()}
      </>
    );
  }

  // 2. HOD Dashboard
  if (isHodView) {
    return (
      <>
        <div className="card p-8 animate-fade-in space-y-6">
        <h1 className="section-title">HOD Dashboard</h1>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600 dark:text-slate-400 font-sans">Loading department information...</p>
          </div>
        ) : (
          <>
            {/* Department Info */}
            {departmentInfo && (
              <div className="material-surface p-6 mb-4">
                <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-2 font-sans">
                  📚 {departmentInfo.name} ({departmentInfo.code})
                </h2>
                {departmentInfo.description && (
                  <p className="text-blue-800 dark:text-blue-200 text-sm font-sans">{departmentInfo.description}</p>
                )}
              </div>
            )}

            {/* Research dashboard (stats) — shown directly. */}
            {renderStatsSection()}

            {/* Faculty Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <StatCard
                title="Total Faculty"
                value={departmentFaculty.length}
                icon="👥"
                color="green"
              />
              <StatCard
                title="Profiles Submitted"
                value={departmentFaculty.filter(f => f.profile_submitted).length}
                color="purple"
                icon="✓"
              />
              <StatCard
                title="Pending"
                value={departmentFaculty.filter(f => !f.profile_submitted).length}
                color="orange"
                icon="⏳"
              />
            </div>

            {/* Faculty List */}
            <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
              <h2 className="section-title text-xl font-sans">Department Faculty</h2>

              {departmentFaculty.length === 0 ? (
                <div className="bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg p-6 text-center">
                  <p className="text-slate-600 dark:text-slate-400 font-sans">No faculty members found in your department.</p>
                </div>
              ) : (
                <div className="overflow-x-auto card">
                  <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                    <thead className="bg-slate-100 dark:bg-slate-800">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Designation</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Joining Date</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider font-sans">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                      {departmentFaculty.map((faculty) => (
                        <tr key={faculty.user_id} className="hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-slate-900 dark:text-slate-50 font-sans">
                              {faculty.full_name || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-600 dark:text-slate-400 font-sans">{faculty.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-900 dark:text-slate-50 font-sans">
                              {faculty.current_designation || "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-slate-600 dark:text-slate-400 font-sans">
                              {faculty.date_of_joining ? new Date(faculty.date_of_joining).toLocaleDateString() : "N/A"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {faculty.profile_submitted ? (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                                ✓ Submitted
                              </span>
                            ) : (
                              <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200">
                                ⏳ Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link
                              to={`/report?facultyUserId=${faculty.user_id}`}
                              className="inline-flex items-center rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
                            >
                              View Profile
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {renderDetailModal()}
    </>
    );
  }

  // 3. Faculty Dashboard
  return (
    <>
      <div className="card p-8 animate-fade-in space-y-6">
      <div>
        <h1 className="section-title">My Dashboard</h1>
        <p className="text-sm text-slate-500 font-sans">Overview of your profile records</p>
      </div>

      {renderStatsSection()}

      {/* Quick Actions Section */}
      <div className="border-t border-slate-200 dark:border-slate-800 pt-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6 font-sans">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Link
            to="/profile"
            className="group flex items-center justify-between p-5 bg-gradient-to-br from-blue-50 to-sky-100 dark:from-slate-800 dark:to-slate-700 hover:from-blue-100 hover:to-sky-200 dark:hover:from-slate-700 dark:hover:to-slate-600 border border-blue-200 dark:border-slate-600 rounded-xl transition-all duration-200"
          >
            <div>
              <p className="font-semibold text-slate-900 dark:text-slate-50 font-sans">Update Profile</p>
              <p className="text-sm text-slate-600 dark:text-slate-300 font-sans">Edit your faculty information</p>
            </div>
            <svg className="w-5 h-5 text-slate-500 dark:text-slate-300 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
          <Link
            to="/report"
            className="group flex items-center justify-between p-5 bg-gradient-to-br from-emerald-50 to-teal-100 dark:from-slate-800 dark:to-slate-700 hover:from-emerald-100 hover:to-teal-200 dark:hover:from-slate-700 dark:hover:to-slate-600 border border-emerald-200 dark:border-slate-600 rounded-xl transition-all duration-200"
          >
            <div>
              <p className="font-semibold text-slate-900 dark:text-slate-50 font-sans">View Profile</p>
              <p className="text-sm text-slate-600 dark:text-slate-300 font-sans">Open your complete profile report</p>
            </div>
            <svg className="w-5 h-5 text-slate-500 dark:text-slate-300 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </div>
    </div>
    {renderDetailModal()}
  </>
  );
}

/* ======================
   MAIN APP
====================== */

function App() {
  const [isDarkMode, setIsDarkMode] = useState(
    localStorage.getItem("theme") === "dark"
  );

  const muiTheme = isDarkMode ? darkTheme : lightTheme;

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <CustomThemeProvider>
        <Router>
          <Routes>
            <Route path="/" element={<Login />} />
            <Route
              path="/dashboard"
              element={
                <Layout>
                  <Dashboard />
                </Layout>
              }
            />
            <Route
              path="/profile"
              element={
                <Layout>
                  <Profile />
                </Layout>
              }
            />
            <Route
              path="/report"
              element={
                <Layout>
                  <FullReport />
                </Layout>
              }
            />
            <Route
              path="/appraisal"
              element={
                <Layout>
                  <Appraisal />
                </Layout>
              }
            />
            <Route
              path="/admin"
              element={
                <Layout>
                  <AdminPanel />
                </Layout>
              }
            />
            <Route
              path="/access"
              element={
                <Layout>
                  <RoleAccessManagement />
                </Layout>
              }
            />
            <Route
              path="/logs"
              element={
                <Layout>
                  <Logs />
                </Layout>
              }
            />
            <Route
              path="/database"
              element={
                <Layout>
                  <DatabaseViewer />
                </Layout>
              }
            />
          </Routes>
        </Router>
      </CustomThemeProvider>
    </MuiThemeProvider>
  );
}

export default App;

/* StatCard Component */
function StatCard({ title, value, color, icon }) {
  const colorConfig = {
    blue: { bg: "from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800", border: "border-blue-200 dark:border-blue-700", icon: "bg-blue-500" },
    green: { bg: "from-green-50 to-green-100 dark:from-green-900 dark:to-green-800", border: "border-green-200 dark:border-green-700", icon: "bg-green-500" },
    purple: { bg: "from-purple-50 to-purple-100 dark:from-purple-900 dark:to-purple-800", border: "border-purple-200 dark:border-purple-700", icon: "bg-purple-500" },
    orange: { bg: "from-orange-50 to-orange-100 dark:from-orange-900 dark:to-orange-800", border: "border-orange-200 dark:border-orange-700", icon: "bg-orange-500" }
  };

  const config = colorConfig[color];

  return (
    <div className={`bg-gradient-to-br ${config.bg} border ${config.border} rounded-lg p-6 hover:shadow-lg transition-shadow`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold uppercase text-slate-700 dark:text-slate-300">{title}</p>
          <p className="text-3xl font-bold text-slate-900 dark:text-slate-50 mt-2">{value}</p>
        </div>
        <div className={`${config.icon} rounded-full p-3 text-white text-2xl`}>
          {icon}
        </div>
      </div>
    </div>
  );
}
